import { useCallback, useEffect, useMemo, useState, useTransition } from "react";
import dayjs from "dayjs";
import axios from "axios";
import {
  normalizeBaseUrl,
  isWeekendIso,
  apiDayToGridCell,
  buildRulesMap,
  buildWeeklyRulesMap,
  getDayKeyFromIso,
  calculateRulesMinutesForDay,
  getWorkingMinMaxFromCellText,
  isTimeCellComplete,
  shouldShowDayCellByFilters,
} from "../components/attendance/attendanceUtils";

import { SupervisorUid } from "../components/constants";

export function useAttendanceData({
  hideWeekends,
  selectedEmps,
  selectedLeaves,
  selectedTimingFilter,
}) {
  const [isPending, startTransition] = useTransition();

  const [loading, setLoading] = useState(false);
  const [machineLoading, setMachineLoading] = useState(false);

  const [fromDate, setFromDate] = useState(() => dayjs().startOf("day"));
  const [toDate, setToDate] = useState(() => dayjs().endOf("day"));
  const [appliedFromDate, setAppliedFromDate] = useState(() =>
    dayjs().startOf("day"),
  );
  const [appliedToDate, setAppliedToDate] = useState(() =>
    dayjs().endOf("day"),
  );

  const [fetchCount, setFetchCount] = useState(0);  // 21/5/26

  const [allMonths, setAllMonths] = useState(false);
  const [appliedAllMonths] = useState(false);

  const [monthData, setMonthData] = useState({});
  const [restLeavesByEmp, setRestLeavesByEmp] = useState({});
  const [leaveOptionsCache, setLeaveOptionsCache] = useState([
    { leaveid: "DAWAM", leavename: "working" },
  ]);

  // ✅ separate master employees list for autocomplete
  const [allEmployees, setAllEmployees] = useState([]);

  const reportGridFields = useMemo(
    () => [
      { key: "attendance", label: "Attendance" },
      { key: "supposedSchedule", label: "Supposed Schedule" },
      { key: "workingHours", label: "Working Hours" },
      { key: "additionRetentionHours", label: "Addition/Retention Hours" },
      {
        key: "additionRetentionAfterRules",
        label: "Addition / Retention After Rules",
      },
      { key: "leave", label: "Leave" },
    ],
    [],
  );

  const timingFilterOptions = useMemo(
    () => [
      { key: "LATE_LEAVING", label: "Late leaving" },
      { key: "LATE_COMING", label: "Late coming" },
      { key: "EARLY_LEAVING", label: "Early leaving" },
      { key: "EARLY_COMING", label: "Early coming" },
      { key: "NOT_ATTENDANT", label: "Not attente" },
    ],
    [],
  );

  const handleApplyDates = useCallback(async (newFrom, newTo) => {
    setLoading(true);
    startTransition(() => {
        setAppliedFromDate(newFrom);
        setAppliedToDate(newTo);
        setFetchCount(prev => prev + 1);  // ← always forces re-fetch 21/5/26
    });
    setLoading(false);
}, []);

  const safeRange = useMemo(() => {
    let a = appliedFromDate.startOf("day");
    let b = appliedToDate.startOf("day");

    if (b.isBefore(a)) [a, b] = [b, a];  // swap if wrong order 

    if (appliedAllMonths) {
      a = a.startOf("month");
      b = b.endOf("month").startOf("day");
    }

    return { start: a, end: b };
  }, [appliedFromDate, appliedToDate, appliedAllMonths]);

  const daysInRange = useMemo(() => {
    const days = [];
    let cur = safeRange.start.clone();
    const end = allMonths
      ? safeRange.end.endOf("month")
      : safeRange.end.endOf("day");

    while (cur.isBefore(end) || cur.isSame(end, "day")) {
      days.push(cur.format("YYYY-MM-DD"));
      cur = cur.add(1, "day");
    }

    return days;
  }, [safeRange, allMonths]);

  const visibleDays = useMemo(() => {
    return hideWeekends
      ? daysInRange.filter((iso) => !isWeekendIso(iso))
      : daysInRange;
  }, [daysInRange, hideWeekends]);

  // ✅ fetch leave types
  useEffect(() => {
    const rawBase = localStorage.getItem("saved_sett_api_url");
    const API_BASE_URL = normalizeBaseUrl(rawBase);
    if (!API_BASE_URL) return;

    axios
      .get(`${API_BASE_URL}/api/vacation-types`, { withCredentials: true })
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data.map((r) => ({
              leaveid: r.LeaveId ?? r.leaveid ?? r.Code,
              leavename: r.LeaveName ?? r.leavename ?? r.ArbStringValue ?? "",
            }))
          : [];

        setLeaveOptionsCache([
          { leaveid: "DAWAM", leavename: "working" },
          ...list,
        ]);
      })
      .catch(() =>
        setLeaveOptionsCache([{ leaveid: "DAWAM", leavename: "working" }]),
      );
  }, []);

  // ✅ fetch all employees once for autocomplete
  useEffect(() => {
    let cancelled = false;

    const fetchEmployees = async () => {
      try {
        const rawBase = localStorage.getItem("saved_sett_api_url");
        const API_BASE_URL = normalizeBaseUrl(rawBase);
        if (!API_BASE_URL) return;

        // change endpoint if your backend uses another route
        const res = await axios.get(`${API_BASE_URL}/api/employees`, {
          params: { excludeUids: SupervisorUid.join(",") },
          withCredentials: true,
        });

        if (cancelled) return;

        const rows = Array.isArray(res.data) ? res.data : [];

        const mapped = rows
          .map((emp) => ({
            key: String(
              emp.empId ??
                emp.EmpId ??
                emp.id ??
                emp.ID ??
                emp.employeeid ??
                emp.EmployeeId ??
                "",
            ),
            label: String(
              emp.empName ??
                emp.EmpName ??
                emp.name ??
                emp.EmployeeName ??
                emp.FullName ??
                "",
            ),
          }))
          .filter((x) => x.key && x.label);

        setAllEmployees(mapped);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setAllEmployees([]);
      }
    };

    fetchEmployees();

    return () => {
      cancelled = true;
    };
  }, []);

  // ✅ fetch attendance by date only, not by selected employees
  useEffect(() => {
    let cancelled = false;

    const fetchRange = async () => {
      setLoading(true);

      try {
        const rawBase = localStorage.getItem("saved_sett_api_url");
        const API_BASE_URL = normalizeBaseUrl(rawBase);

        if (!API_BASE_URL) {
          if (!cancelled) {
            setMonthData({});
            setRestLeavesByEmp({});
          }
          return;
        }

        const from = safeRange.start.format("YYYY-MM-DD");
        const to = safeRange.end.format("YYYY-MM-DD");

        const params = { from, to, excludeUids: SupervisorUid.join(",") };

        const selectedEmpIds = selectedEmps
          .map((x) => x?.key)
          .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
          .map((v) => String(v));

        if (selectedEmpIds.length > 0) {
          params.empid = selectedEmpIds.join(",");
        }
        
        // res contain the json returned from flask , id,name,days(site,schedule,leave)
        const res = await axios.get(`${API_BASE_URL}/api/attendances`, {
          params,
          withCredentials: true,
        });

        if (cancelled) return;

        const rows = Array.isArray(res.data) ? res.data : [];
        const agg = {};  // temporary obj to organize api before storing it in monthData
        const leaveAgg = {};

        rows.forEach((emp) => {
          const key = String(emp.empId ?? emp.androidId ?? emp.empName ?? "");
          if (!key) return;

          const weeklyRulesMap = buildWeeklyRulesMap(
            emp.weeklyRules ?? emp.WeeklyRules ?? emp.weekly_rules ?? null,
          );

          agg[key] = {
            employee: emp.empName ?? key,
            empId: emp.empId ?? key,
            days: {},
            weeklyRulesMap,
          };

          leaveAgg[String(emp.empId ?? key)] = emp.leavenb || {
            empid: emp.empId ?? key,
            items: [],
          };

          const daysObj = emp.days || {};
          
          // process each day
          Object.keys(daysObj).forEach((iso) => {
            const d = daysObj[iso] || {};
            const cell = apiDayToGridCell(d, leaveOptionsCache);
            // combines d= site + schedule + leave into one cell

            const rawRuleTime =
              d?.schedule?.rulestime ??
              d?.leave?.rulestime ??
              d?.rulestime ??
              cell?.rulestime ??
              null;

            let ruleDayMap = Array.isArray(rawRuleTime)
              ? buildRulesMap(rawRuleTime)
              : rawRuleTime && typeof rawRuleTime === "object"
                ? rawRuleTime
                : {};

            const scheduleText = String(d?.schedule?.timeSch || "").trim();
            if (Object.keys(ruleDayMap).length === 0 && scheduleText) {
              const dayKey = getDayKeyFromIso(iso);
              if (dayKey) {
                ruleDayMap = {
                  [dayKey]: scheduleText
                    .replace(/\s*\|\s*/g, " ~ ")
                    .replace(/\s*~\s*/g, " ~ "),
                };
              }
            }

            agg[key].days[iso] = {
              time: cell.time,                    // combined attendance text
              status: cell.status,
              address: cell.address,
              scheduleText,
              leaveLabel: cell?._src?.leaveLabel,
              sumtimesh: cell.sumtimesh,          // manual schedule range
              rulestime: ruleDayMap,
              rulesMap: ruleDayMap,               // weekly rules
              stampModules: cell.stampModules || "", //add the stamp modules 22/5/26
              stampDates: cell.stampDates || "",
              // agg["37"].days["2026-05-18"] = { time, status, address, ... }
            };
            //* ADD THIS TEMPORARILY:to see stamp value from front end side 
           // console.log("stampModules for", iso, ":", cell.stampModules);
          });
        });

        // Fill missing days with empty attendance but keep supposed schedule
        Object.keys(agg).forEach((empKey) => {
          const emp = agg[empKey];

          // Collect known schedule per day-of-week from returned days
          const scheduleByDow = {};
          Object.keys(emp.days).forEach((iso) => {
            const dayObj = emp.days[iso];
            const dayKey = getDayKeyFromIso(iso);
            if (dayKey && !scheduleByDow[dayKey]) {
              const sched = dayObj.rulestime?.[dayKey] || dayObj.scheduleText;
              if (sched) scheduleByDow[dayKey] = sched;
            }
          });

          // Insert placeholder for every day in range that had no record
          let cur = safeRange.start.clone();
          while (cur.isBefore(safeRange.end) || cur.isSame(safeRange.end, "day")) {
            const iso = cur.format("YYYY-MM-DD");
            if (!emp.days[iso]) {
              const dayKey = getDayKeyFromIso(iso);
              const sched = scheduleByDow[dayKey] || "";
              emp.days[iso] = {
                time: "",
                status: null,
                address: "",
                scheduleText: sched,
                leaveLabel: "",
                sumtimesh: null,
                rulestime: sched ? { [dayKey]: sched } : {},
                rulesMap: sched ? { [dayKey]: sched } : {},
              };
            }
            cur = cur.add(1, "day");
          }
        });

        // Fetch rules and compute scheduleAfterRulesMinutes for each day
        try {
          const rulesParams = { from, to };
          if (selectedEmpIds.length > 0) {
            rulesParams.empids = selectedEmpIds.join(",");
          }

          const rulesRes = await axios.get(`${API_BASE_URL}/api/calculaterules`, {
            params: rulesParams,
            withCredentials: true,
          });

          const apiRules =
            rulesRes?.data && typeof rulesRes.data === "object" ? rulesRes.data : {};

          Object.keys(agg).forEach((empKey) => {
            const emp = agg[empKey];
            const empId = String(emp?.empId ?? empKey);
            const empRulesMap = apiRules?.[empId] || {};
            const mergedRuleRows = Object.values(empRulesMap).flat();

            Object.keys(emp.days).forEach((iso) => {
              const dayObj = emp.days[iso];
              const dayKey = getDayKeyFromIso(iso);

              const existingRulesMap =
                dayObj?.rulestime && typeof dayObj.rulestime === "object"
                  ? { ...dayObj.rulestime }
                  : {};

              let normalizedRuleText = String(existingRulesMap?.[dayKey] ?? "")
                .trim()
                .replace(/\s*\|\s*/g, " ~ ")
                .replace(/\s*~\s*/g, " ~ ");

              if (!normalizedRuleText) {
                const matchedRow = mergedRuleRows.find(
                  (r) => String(r?.DayList || "").trim() === dayKey && r?.WorkTime,
                );
                if (matchedRow) {
                  normalizedRuleText = String(matchedRow.WorkTime)
                    .trim()
                    .replace(/\s*\|\s*/g, " ~ ")
                    .replace(/\s*~\s*/g, " ~ ");
                }
              }

              if (!normalizedRuleText) normalizedRuleText = "—";

              const updatedRulestime = { ...existingRulesMap, [dayKey]: normalizedRuleText };

              const calc = calculateRulesMinutesForDay(
                { ...dayObj, rulestime: updatedRulestime },
                iso,
                mergedRuleRows,
                leaveOptionsCache,
              );

              emp.days[iso] = {
                ...dayObj,
                rulestime: updatedRulestime,
                rulesMap: existingRulesMap,
                scheduleAfterRulesMinutes: calc.totalMinutes,
                scheduleAfterRulesHours: calc.totalHours,
                scheduleAfterRulesBreakdown: calc.breakdown,
                scheduleAfterRulesWeighted: calc.weighted,
                stampModules: dayObj.stampModules || "",  
              };
              //console.log("AFTER RULES LOOP stampModules:", emp.days[iso].stampModules);
            });

            emp.ruleRows = mergedRuleRows;
          });
        } catch (rulesError) {
          console.error("Error fetching rules:", rulesError);
        }
        
        // monthData is memory of the grid , when click apply it gets completely replaced with fresh data from back 
        setMonthData((prev) => {
          const next = { ...prev };      // copy previous data
          Object.keys(agg).forEach((key) => {
            next[key] = next[key]
              ? {
                  ...next[key],       // merge with existing
                  days: { ...next[key].days, ...agg[key].days },
                  ruleRows: agg[key].ruleRows ?? next[key].ruleRows,
                  weeklyRulesMap: agg[key].weeklyRulesMap ?? next[key].weeklyRulesMap,
                }
              : agg[key];          // or set new employee data
          });
          return next;
        });
        // // ✅ always replace with fresh data 8-6-26
        // setMonthData(agg);

        setRestLeavesByEmp(leaveAgg);

        // ✅ fallback: if /api/employees returned nothing, build autocomplete once from attendance
        setAllEmployees((prev) => {
          if (prev.length > 0) return prev;

          return Object.entries(agg).map(([key, emp]) => ({
            key: String(key),
            label: emp.employee || String(key),
          }));
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching attendances:", error);
          setMonthData({});
          setRestLeavesByEmp({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRange();

    return () => {
      cancelled = true;
    };
  }, [safeRange,  selectedEmps, fetchCount]);
 // [safeRange, leaveOptionsCache, selectedEmps, fetchCount]);
  // runs every time safeRange changes (after APPLY clicked)

  // ✅ autocomplete options come from master list, not monthData
  const employeeOptions = useMemo(() => {
    return allEmployees;
  }, [allEmployees]);

  const totalEmployees = useMemo(() => Object.keys(monthData).length, [monthData]);

  const filteredKeys = useMemo(() => {
    let keys = Object.keys(monthData);

    if (selectedEmps.length > 0) {
      const allowed = new Set(selectedEmps.map((x) => String(x.key)));
      keys = keys.filter((k) => allowed.has(String(k)));
    }

    if (selectedTimingFilter?.key === "NOT_ATTENDANT") {
      keys = keys.filter((empKey) => {
        const emp = monthData[empKey];
        if (!emp?.days) return false;

        return visibleDays.some((iso) => {
          const cell = emp.days?.[iso];
          const cellText = String(cell?.time || "").trim();
          const working = getWorkingMinMaxFromCellText(cellText, leaveOptionsCache);

          // no working at all
          if (!working.hasWork) return true;

          // working exists but incomplete
          return !isTimeCellComplete(cellText);
        });
      });

      return keys;
    }

    if (selectedLeaves.length > 0 || selectedTimingFilter) {
      keys = keys.filter((empKey) => {
        const emp = monthData[empKey];
        if (!emp?.days) return false;

        return Object.entries(emp.days).some(([iso, cell]) =>
          shouldShowDayCellByFilters(
            cell,
            iso,
            selectedLeaves,
            leaveOptionsCache,
            selectedTimingFilter,
          ),
        );
      });
    }

    return keys;
  }, [
    monthData,
    selectedEmps,
    selectedLeaves,
    leaveOptionsCache,
    selectedTimingFilter,
    visibleDays,
  ]);

  return {
    loading: loading || isPending,
    machineLoading,
    monthData,
    setMonthData,
    restLeavesByEmp,
    leaveOptionsCache,
    safeRange,
    visibleDays,
    employeeOptions,
    filteredKeys,
    totalEmployees,
    fromDate,
    toDate,
    appliedFromDate,
    appliedToDate,
    setFromDate,
    setToDate,
    handleApplyDates,
    allMonths,
    setAllMonths,
    appliedAllMonths,
    timingFilterOptions,
    reportGridFields,
  };
}